// @ts-ignore esModuleInterop
import React, { useState, useMemo } from 'react'
import ms from 'ms'
import type {
  primitives,
  AuctionBlueprintScheduledOnlineReactiveArgs,
} from '@adc/blueprint'
import type { Maybe, Property, Listing } from '@adc/sdk/types'
import { hooks } from '@adc/ui-adc'
import PropertyProvider, {
  PropertyPageActionsType,
  SavedPropertiesDataType,
} from 'common/context/property'
import { AiPromptProvider } from '../context/aiPrompt'
import PropertyHeader from '../modules/propertyHeader'
import MessagesBanner from '../modules/messagesBanner'
import PropertyDetails from '../modules/propertyDetails'
import Breadcrumbs from '../modules/breadcrumbs'
import Documents from '../modules/documents'
import { Faqs } from '../modules/faqs'
import FaqsWithAi from '../modules/faqsWithAi'
import InvestmentInfo from '../modules/investmentInfo'
import TransactionDetails from '../modules/transactionDetails'
import SimilarPropertiesCarousel from '../modules/similarProperties'
import AuctionDetails from '../modules/auctionDetails'
import DueDiligence from '../modules/dueDiligence'
import { StickyHeader } from '../modules/stickyHeader'
import HelpCenter from '../modules/helpCenter'
import { ConnectedCompSales } from '../modules/compsales'
import SeoLinksFooter from '../modules/seoLinksFooter'
import Disclaimer from '../modules/disclaimer'
import AiListingChatWrapper, { AiChatButton } from '../components/aiChat'
import PropertyHistory from '../modules/propertyHistory'
import ChoosePathModal, {
  ChoosePathModalProps,
} from '../modules/redesign/choosePathModal'
import {
  CombinedListingType,
  ListingPageActionsType,
  ListingProvider,
} from '../context/listing'
import ActivePropertyWrapperContainer, {
  ApplicationBidBanner,
} from './activePropertyWrapper'

type AuctionUser = primitives.AuctionUser

interface DefaultPropertyContentProps {
  segment: AuctionBlueprintScheduledOnlineReactiveArgs['segment']
  listing: CombinedListingType
  activeListings: Listing[]
  pageBannerRef: any
  user: AuctionUser
  isOnline: Maybe<boolean> | undefined
  property: Property
  propertyActions: PropertyPageActionsType
  listingActions: ListingPageActionsType
  savedPropertiesData: SavedPropertiesDataType
  pathModal: ChoosePathModalProps
}

const DefaultPropertyContent = ({
  segment,
  listing,
  activeListings,
  pageBannerRef,
  user,
  isOnline,
  property,
  propertyActions,
  listingActions,
  savedPropertiesData,
  pathModal,
}: DefaultPropertyContentProps) => {
  const { useIsMobileHook } = hooks
  const isMobile = useIsMobileHook()

  /**
   * This state has to be managed somewhere higher up so that the AiListingChatWrapper can be
   * can be controlled by the page (e.g. opening via external prompt, or just opening it)
   * For now we'll put this here until a better place is determined.
   */
  const [openChat, setOpenChat] = useState(false)
  const [externalPrompt, setExternalPrompt] = useState<string | undefined>()

  const handleInternalChatClick = (show: boolean) => {
    setOpenChat(show)
  }

  const handleAiPromptClick = (prompt?: string) => {
    setExternalPrompt(prompt)
    if (!prompt && openChat) {
      /**
       * If just opening (no external prompt) and the chat is already open,
       * then close an re-open to get a little ui "wiggle" to indicate something happened
       * and to get the focus into the chat input.
       */
      setOpenChat(false)
      setTimeout(() => {
        setOpenChat(true)
      }, ms('100ms'))
    } else {
      setOpenChat(true)
    }
  }

  // TODO: check if segment is used in any components that assume it's live auction segment and point those to live_segment instead, once sure it's safe, change segment to only populate if it's OnlineAuctionSegment typename
  return (
    <PropertyProvider
      property={property}
      propertyActions={propertyActions}
      savedPropertiesData={savedPropertiesData}
    >
      <ListingProvider
        listing={listing}
        segment={segment}
        activeListings={activeListings}
        listingActions={listingActions}
      >
        <AiPromptProvider onClickAiPrompt={handleAiPromptClick}>
          <StickyHeader />
          {!isMobile && <Breadcrumbs />}
          <MessagesBanner pageBannerRef={pageBannerRef} />
          <ChoosePathModal {...pathModal} />
          <ApplicationBidBanner listing={listing} segment={segment} />
          <PropertyHeader />
          <AiChatButton
            setOpenChat={handleInternalChatClick}
            openChat={openChat}
          />
          <AiListingChatWrapper
            listing={listing}
            openChat={openChat}
            setOpenChat={setOpenChat}
            setExternalPrompt={setExternalPrompt}
            externalPrompt={externalPrompt}
            mode='chat'
          />
          <InvestmentInfo />
          {/** TODO: may want to make this part of context or hook */}
          <AuctionDetails onClickAiPrompt={handleAiPromptClick} />
          <FaqsWithAi onClickAiPrompt={handleAiPromptClick} listing={listing} />
          <PropertyDetails key={String(user.logged_in)} />
          <PropertyHistory />
          {/* @ts-ignore js redux file */}
          <SimilarPropertiesCarousel />
          <ConnectedCompSales />

          {/* @ts-ignore segment type is wrong */}
          <DueDiligence segment={segment} isOnline={isOnline} />
          <Documents />
          <Faqs />
          <TransactionDetails />
          <HelpCenter />
          <Disclaimer />
          {isMobile && <Breadcrumbs />}
          <SeoLinksFooter />
        </AiPromptProvider>
      </ListingProvider>
    </PropertyProvider>
  )
}

interface FunctionalPropertyContainerProps {
  pageBannerRef: any
}

const FunctionalPropertyContainer = (
  props: FunctionalPropertyContainerProps
) => {
  // Owned here — above PropertyWithSegment — because selecting a path can flip
  // the current listing between online and live auctions, remounting the whole
  // property subtree. Keeping the interaction state at this stable level makes
  // the choose-a-path modal a controlled component that stays dismissed across
  // those remounts (show once per visit).
  const [hasInteractedWithPathModal, setHasInteractedWithPathModal] =
    useState(false)
  const pathModal = useMemo<ChoosePathModalProps>(
    () => ({
      hasInteracted: hasInteractedWithPathModal,
      onInteract: () => setHasInteractedWithPathModal(true),
    }),
    [hasInteractedWithPathModal]
  )

  return (
    <ActivePropertyWrapperContainer
      {...props}
      pathModal={pathModal}
      childComponent={DefaultPropertyContent}
    />
  )
}

export default FunctionalPropertyContainer
